import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarNiveles,
  crearNivel,
  actualizarNivel,
  eliminarNivel,
} from "../../../../services/nivelesService";
import { showToast } from "../../../../utils/showToast";

const buildTempId = (() => {
  let counter = 0;
  return () => `temp-nivel-${counter++}`;
})();

const normalizeNivel = (nivel = {}, fallbackId) => ({
  id: nivel.id ?? fallbackId ?? buildTempId(),
  nombre: nivel.nombre?.trim() || "Sin nombre",
  descripcion: nivel.descripcion?.trim() || "",
  activo:
    typeof nivel.activo === "boolean"
      ? nivel.activo
      : nivel.activo === 1 || nivel.activo === "1",
});

const seedNiveles = [
  { id: 1, nombre: "Nivel I", descripcion: "Modalidad Fija", activo: 1 },
  { id: 2, nombre: "Nivel II", descripcion: "Modalidad Movil", activo: 1 },
  { id: 3, nombre: "Nivel III", descripcion: "Modalidad Especializada", activo: 0 },
].map((nivel, index) => normalizeNivel(nivel, `seed-${index}`));

const initialFormState = {
  nombre: "",
  descripcion: "",
  activo: true,
};

const buildPayload = (form) => ({
  nombre: form.nombre?.trim() || "",
  descripcion: form.descripcion?.trim() || null,
  activo: form.activo ? 1 : 0,
});

const validarNivel = (form) => {
  if (!form.nombre?.trim()) return "El nombre del nivel es obligatorio.";
  return null;
};

const extractNivel = (response, fallback) => {
  if (response?.nivel) return normalizeNivel(response.nivel);
  if (response) return normalizeNivel(response);
  return normalizeNivel(fallback);
};

export default function useNivelesLogic() {
  const [niveles, setNiveles] = useState(seedNiveles);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modal, setModal] = useState("");
  const [nivelSeleccionado, setNivelSeleccionado] = useState(null);
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filtro, setFiltro] = useState("total");

  const fetchNiveles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarNiveles();
      const normalized = (Array.isArray(data) ? data : []).map(normalizeNivel);
      setNiveles(normalized);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo cargar la lista de niveles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNiveles();
  }, [fetchNiveles]);

  const resumen = useMemo(() => {
    const total = niveles.length;
    const activos = niveles.filter((nivel) => nivel.activo).length;
    const inactivos = total - activos;
    return { total, activos, inactivos };
  }, [niveles]);

  const nivelesFiltrados = useMemo(() => {
    if (filtro === "activos") return niveles.filter((nivel) => nivel.activo);
    if (filtro === "inactivos") return niveles.filter((nivel) => !nivel.activo);
    return niveles;
  }, [niveles, filtro]);

  const aplicarFiltro = useCallback((key) => {
    setFiltro((prev) => (prev === key ? "total" : key));
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  const abrirModalNuevo = useCallback(() => {
    setFormNuevo(initialFormState);
    setNivelSeleccionado(null);
    setModal("nuevo");
  }, []);

  const abrirModalEditar = useCallback((nivel) => {
    const normalized = normalizeNivel(nivel);
    setNivelSeleccionado(normalized);
    setFormEditar({
      ...initialFormState,
      nombre: normalized.nombre,
      descripcion: normalized.descripcion,
      activo: normalized.activo,
    });
    setModal("editar");
  }, []);

  const abrirModalEliminar = useCallback((nivel) => {
    setNivelSeleccionado(normalizeNivel(nivel));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setNivelSeleccionado(null);
    setFormNuevo(initialFormState);
    setFormEditar(initialFormState);
  }, []);

  const handleCrearNivel = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const validation = validarNivel(formNuevo);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingNuevo(true);
      try {
        const payload = buildPayload(formNuevo);
        const nuevo = await crearNivel(payload);
        const fallback = {
          nombre: formNuevo.nombre,
          descripcion: formNuevo.descripcion,
          activo: formNuevo.activo,
        };
        const nivelNormalizado = extractNivel(nuevo, fallback);
        setNiveles((prev) => [...prev, nivelNormalizado]);
        setLastUpdated(new Date());
        showToast({ type: "success", message: "Nivel creado correctamente." });
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo crear el nivel.",
        });
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, cerrarModal]
  );

  const handleActualizarNivel = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!nivelSeleccionado) return;

      const validation = validarNivel(formEditar);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingEditar(true);
      try {
        const payload = buildPayload(formEditar);
        const actualizado = await actualizarNivel(nivelSeleccionado.id, payload);
        setNiveles((prev) =>
          prev.map((nivel) =>
            nivel.id === nivelSeleccionado.id
              ? extractNivel(actualizado, {
                  ...nivelSeleccionado,
                  ...formEditar,
                  id: nivelSeleccionado.id,
                })
              : nivel
          )
        );
        setLastUpdated(new Date());
        showToast({ type: "success", message: "Nivel actualizado correctamente." });
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo actualizar el nivel.",
        });
      } finally {
        setSavingEditar(false);
      }
    },
    [nivelSeleccionado, formEditar, cerrarModal]
  );

  const handleEliminarNivel = useCallback(async () => {
    if (!nivelSeleccionado) return;
    setDeleting(true);
    try {
      await eliminarNivel(nivelSeleccionado.id);
      setNiveles((prev) => prev.filter((nivel) => nivel.id !== nivelSeleccionado.id));
      setLastUpdated(new Date());
      showToast({ type: "success", message: "Nivel eliminado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo eliminar el nivel.",
      });
    } finally {
      setDeleting(false);
    }
  }, [nivelSeleccionado, cerrarModal]);

  return {
    niveles: nivelesFiltrados,
    nivelesFiltrados,
    filtro,
    aplicarFiltro,
    loading,
    error,
    resumen,
    lastUpdated,
    dismissError,
    reload: fetchNiveles,
    modal,
    nivelSeleccionado,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    handleCrearNivel,
    handleActualizarNivel,
    handleEliminarNivel,
  };
}
